import { RANGES, isDesktopShell, limiterOptions, settings, shownLimiter, updateSettings } from "../../../settings";
import type { Settings } from "../../../settings";
import { Segmented } from "../../components/Segmented";
import type { SegmentedOption } from "../../components/Segmented";
import { Select } from "../../components/Select";
import { Slider } from "../../components/Slider";
import { Toggle } from "../../components/Toggle";

type VideoSettings = Settings["video"];

const QUALITY_OPTIONS: readonly SegmentedOption<VideoSettings["quality"]>[] = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "ultra", label: "Ultra" },
];

const ANTIALIAS_OPTIONS: readonly SegmentedOption<VideoSettings["antialias"]>[] = [
  { value: "off", label: "Off" },
  { value: "fxaa", label: "FXAA" },
];

const DESKTOP = isDesktopShell();
const FRAME_LIMITER_OPTIONS = limiterOptions(DESKTOP);
const FRAME_LIMITER_HELP = DESKTOP
  ? "Unlimited renders as fast as your GPU allows."
  : "VSync matches your display's refresh rate, and a browser never renders faster. The desktop app runs unlimited.";

export function VideoTab() {
  const v = settings.value.video;
  const set = (patch: Partial<VideoSettings>) => updateSettings({ video: patch });

  return (
    <div class="settings__content">
      <h3 class="settings__group">Camera</h3>
      <div class="settings__rows">
        <Slider
          label="Field of view"
          description="Horizontal, at 16:9."
          unit="°"
          {...RANGES.fov}
          value={v.fov}
          onChange={(fov) => set({ fov })}
        />
        <Slider
          label="Viewmodel field of view"
          description="Size of the weapon in your hands."
          unit="°"
          {...RANGES.viewmodelFov}
          value={v.viewmodelFov}
          onChange={(viewmodelFov) => set({ viewmodelFov })}
        />
        <Toggle
          label="Show weapon model"
          description="Draws the weapon in your hands."
          checked={v.showViewmodel}
          onChange={(showViewmodel) => set({ showViewmodel })}
        />
        <Toggle
          label="Dynamic field of view"
          description="Widens the view slightly while you move fast."
          checked={v.dynamicFov}
          onChange={(dynamicFov) => set({ dynamicFov })}
        />
      </div>

      <h3 class="settings__group">Rendering</h3>
      <div class="settings__rows">
        <Slider
          label="Render scale"
          description="Internal resolution. Lower is faster, higher is sharper."
          unit="x"
          {...RANGES.renderScale}
          value={v.renderScale}
          onChange={(renderScale) => set({ renderScale })}
        />
        <Segmented
          label="Quality"
          description="Overall detail preset. Low also turns off bloom, anti-aliasing and the colour grade."
          value={v.quality}
          options={QUALITY_OPTIONS}
          onChange={(quality) => set({ quality })}
        />
        <Segmented
          label="Anti-aliasing"
          description="Smooths jagged edges."
          value={v.antialias}
          options={ANTIALIAS_OPTIONS}
          onChange={(antialias) => set({ antialias })}
        />
        <Select
          label="Frame limiter"
          description={FRAME_LIMITER_HELP}
          value={shownLimiter(v.frameLimiter, DESKTOP)}
          options={FRAME_LIMITER_OPTIONS}
          onChange={(frameLimiter) => set({ frameLimiter })}
        />
      </div>

      <h3 class="settings__group">Effects</h3>
      <div class="settings__rows">
        <Toggle
          label="Shadows"
          checked={v.shadows}
          onChange={(shadows) => set({ shadows })}
        />
        <Toggle label="Bloom" checked={v.bloom} onChange={(bloom) => set({ bloom })} />
        <Toggle label="Film grain" checked={v.filmGrain} onChange={(filmGrain) => set({ filmGrain })} />
        <Toggle label="Vignette" checked={v.vignette} onChange={(vignette) => set({ vignette })} />
      </div>
    </div>
  );
}
